<script>
    import { onMount, onDestroy } from 'svelte';
    
    // Component state
    let loading = false;
    let error = '';
    let classCount = 0;
    let lastFetched = null;
    let totalRevenue = 0;
    let totalTeacherPayments = 0;
    let classesWithoutTeacher = [];
    
    // Async job state
    let jobId = null;
    let jobStatus = null;
    let progressMessage = '';
    let totalClasses = 0;
    let processedClasses = 0;
    let statusCheckInterval = null;
    
    // Month selection state
    let selectedMonth = '';
    let availableMonths = [];
    
    // Generate available months from September 2024 to December 2026
    function generateAvailableMonths() {
        const months = [];
        const startDate = new Date(2024, 8); // September 2024
        const endDate = new Date(2026, 11); // December 2026
        
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const monthName = currentDate.toLocaleDateString('lt-LT', { month: 'long', year: 'numeric' });
            const monthValue = `${year}-${String(month + 1).padStart(2, '0')}`;
            
            months.push({
                value: monthValue,
                label: monthName
            });
            
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
        
        return months;
    }
    
    onMount(() => {
        availableMonths = generateAvailableMonths();
    });
    
    onDestroy(() => {
        // Clean up interval when component is destroyed
        if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
        }
    });
    
    // Start CSV export job
    async function startTeacherRevenuePaymentCSV() {
        loading = true;
        error = '';
        jobId = null;
        jobStatus = null;
        progressMessage = '';
        totalRevenue = 0;
        totalTeacherPayments = 0;
        classesWithoutTeacher = [];
        
        // Clear any existing interval
        if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
            statusCheckInterval = null;
        }
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                loading = false;
                return;
            }
            
            // Build URL with optional month parameter
            let url = '/studentplayground/api/admin/teacher-revenue-payment-csv/';
            if (selectedMonth) {
                url += `?selected_month=${selectedMonth}`;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko pradėti eksporto';
                loading = false;
                return;
            }
            
            const result = await response.json();
            if (result.status === 'success') {
                jobId = result.job_id;
                jobStatus = 'pending';
                progressMessage = 'Starting export...';
                
                // Start polling for status
                checkJobStatus();
                statusCheckInterval = setInterval(checkJobStatus, 2000); // Check every 2 seconds
            } else {
                error = result.message || 'Netikėta klaida';
                loading = false;
            }
            
        } catch (err) {
            console.error('Error starting CSV export:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
            loading = false;
        }
    }
    
    // Check job status
    async function checkJobStatus() {
        if (!jobId) return;
        
        try {
            const token = localStorage.getItem('jwtToken');
            const url = `/studentplayground/api/admin/teacher-revenue-payment-csv-status/${jobId}/`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                console.error('Failed to check job status');
                return;
            }
            
            const result = await response.json();
            jobStatus = result.status;
            progressMessage = result.progress_message || '';
            totalClasses = result.total_classes || 0;
            processedClasses = result.processed_classes || 0;
            
            if (result.status === 'completed') {
                // Stop polling
                if (statusCheckInterval) {
                    clearInterval(statusCheckInterval);
                    statusCheckInterval = null;
                }
                
                loading = false;
                classCount = result.total_count || 0;
                classesWithoutTeacher = result.classes_without_teacher || [];
                lastFetched = new Date();
                
                // Calculate totals
                if (result.data) {
                    result.data.forEach(item => {
                        totalRevenue += item.total_student_price || 0;
                        totalTeacherPayments += item.teacher_payment || 0;
                    });
                }
                
                // Generate and download CSV
                generateAndDownloadCSV(result.data || []);
            } else if (result.status === 'failed') {
                // Stop polling
                if (statusCheckInterval) {
                    clearInterval(statusCheckInterval);
                    statusCheckInterval = null;
                }
                
                loading = false;
                error = result.error_message || 'Export failed';
            }
            // If status is 'pending' or 'processing', continue polling
            
        } catch (err) {
            console.error('Error checking job status:', err);
        }
    }
    
    // Generate CSV content and trigger download
    function generateAndDownloadCSV(data) {
        // Define CSV headers
        const headers = [
            'Class ID',
            'Class Name',
            'Class Date',
            'Teacher Name',
            'Teacher ID',
            'Total Student Price (€)',
            'Teacher Payment (€)',
            'Student Count',
            'Matched Charges Count',
            'Google Calendar Event ID'
        ];
        
        // Create CSV content
        let csvContent = headers.join(',') + '\n';
        
        data.forEach(item => {
            const row = [
                item.class_id || '',
                `"${(item.class_name || '').replace(/"/g, '""')}"`,  // Escape quotes
                item.class_date || '',
                `"${(item.teacher_name || '').replace(/"/g, '""')}"`,  // Escape quotes
                item.teacher_id || '',
                item.total_student_price || 0,
                item.teacher_payment || 0,
                item.student_count || 0,
                item.matched_charges_count || 0,
                item.gcal_event_id || ''
            ];
            csvContent += row.join(',') + '\n';
        });
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        const now = new Date();
        const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD format
        const monthSuffix = selectedMonth ? `_${selectedMonth}` : '';
        const filename = `teacher_revenue_payment${monthSuffix}_${timestamp}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
</script>

<style>
    .container {
        padding: 20px;
        max-width: 1000px;
        margin: 0 auto;
    }
    
    .header {
        margin-bottom: 30px;
    }
    
    h2 {
        color: #333;
        margin-bottom: 10px;
    }
    
    .description {
        color: #666;
        font-size: 14px;
        margin-bottom: 20px;
        line-height: 1.6;
    }
    
    .month-selector-section {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .month-selector-section h3 {
        color: #333;
        font-size: 18px;
        margin-bottom: 15px;
    }
    
    .month-selector-wrapper {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
    }
    
    .month-selector-label {
        font-weight: 600;
        color: #495057;
    }
    
    .month-selector {
        padding: 8px 12px;
        border: 1px solid #ced4da;
        border-radius: 5px;
        font-size: 14px;
        min-width: 200px;
        cursor: pointer;
    }
    
    .month-selector:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
    }
    
    .clear-month-button {
        padding: 8px 16px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s;
    }
    
    .clear-month-button:hover {
        background: #5a6268;
    }
    
    .download-section {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 30px;
        margin-bottom: 20px;
    }
    
    .download-button {
        background: #007bff;
        color: white;
        border: none;
        border-radius: 5px;
        padding: 12px 24px;
        font-size: 16px;
        cursor: pointer;
        transition: background-color 0.2s;
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }
    
    .download-button:hover:not(:disabled) {
        background: #0056b3;
    }
    
    .download-button:disabled {
        background: #6c757d;
        cursor: not-allowed;
        opacity: 0.6;
    }
    
    .download-icon {
        font-size: 18px;
    }
    
    .info-box {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .info-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .info-item:last-child {
        border-bottom: none;
    }
    
    .info-label {
        font-weight: 600;
        color: #495057;
    }
    
    .info-value {
        color: #6c757d;
    }
    
    .error-message {
        background: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
        padding: 12px;
        border-radius: 5px;
        margin-bottom: 20px;
    }
    
    .warning-message {
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        color: #856404;
        padding: 12px;
        border-radius: 5px;
        margin-bottom: 20px;
    }
    
    .loading-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #ffffff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    
    .success-message {
        background: #d4edda;
        border: 1px solid #c3e6cb;
        color: #155724;
        padding: 12px;
        border-radius: 5px;
        margin-top: 20px;
    }
    
    .csv-format-info {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
    }
    
    .csv-format-info h3 {
        color: #333;
        font-size: 18px;
        margin-bottom: 15px;
    }
    
    .csv-format-info ul {
        list-style-type: none;
        padding-left: 0;
    }
    
    .csv-format-info li {
        padding: 8px 0;
        color: #495057;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .csv-format-info li:last-child {
        border-bottom: none;
    }
    
    .csv-format-info code {
        background: #f8f9fa;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
        color: #e83e8c;
    }
</style>

<div class="container">
    <div class="header">
        <h2>💰 Teacher Revenue vs Payment CSV Export</h2>
        <p class="description">
            Download a CSV file of all past classes with student pricing and teacher payment data. 
            Each row represents one class with calculated student revenue and teacher payment amounts.
        </p>
    </div>
    
    {#if error}
        <div class="error-message">
            ⚠️ {error}
        </div>
    {/if}
    
    {#if classesWithoutTeacher.length > 0}
        <div class="warning-message">
            ⚠️ Warning: {classesWithoutTeacher.length} class(es) could not be matched to a teacher. These classes are included in the export but have "Unknown Teacher" as the teacher name.
        </div>
    {/if}
    
    <div class="month-selector-section">
        <h3>📅 Filter by Month (Optional)</h3>
        <div class="month-selector-wrapper">
            <label class="month-selector-label" for="month-select">Select Month:</label>
            <select 
                id="month-select"
                class="month-selector" 
                bind:value={selectedMonth}
            >
                <option value="">All Months</option>
                {#each availableMonths as month}
                    <option value={month.value}>{month.label}</option>
                {/each}
            </select>
            {#if selectedMonth}
                <button 
                    class="clear-month-button"
                    on:click={() => selectedMonth = ''}
                >
                    Clear Filter
                </button>
            {/if}
        </div>
    </div>
    
    <div class="download-section">
        <button 
            class="download-button" 
            on:click={startTeacherRevenuePaymentCSV}
            disabled={loading}
        >
            {#if loading}
                <span class="loading-spinner"></span>
                <span>Processing...</span>
            {:else}
                <span class="download-icon">⬇️</span>
                <span>Start Export</span>
            {/if}
        </button>
        
        {#if jobStatus === 'processing' || jobStatus === 'pending'}
            <div class="progress-info" style="margin-top: 20px; padding: 15px; background: #e7f3ff; border-radius: 8px;">
                <div style="font-weight: 600; margin-bottom: 10px;">📊 Export in Progress</div>
                <div style="margin-bottom: 8px;">{progressMessage}</div>
                {#if totalClasses > 0}
                    <div style="margin-bottom: 8px;">
                        Progress: {processedClasses} / {totalClasses} classes ({Math.round((processedClasses / totalClasses) * 100)}%)
                    </div>
                    <div style="background: #fff; border-radius: 4px; height: 20px; overflow: hidden; margin-top: 10px;">
                        <div style="background: #007bff; height: 100%; width: {(processedClasses / totalClasses) * 100}%; transition: width 0.3s;"></div>
                    </div>
                {/if}
                <div style="margin-top: 10px; font-size: 12px; color: #666;">
                    This may take a few minutes. Please keep this page open...
                </div>
            </div>
        {/if}
    </div>
    
    {#if lastFetched}
        <div class="info-box">
            <div class="info-item">
                <span class="info-label">Last Downloaded:</span>
                <span class="info-value">{lastFetched.toLocaleString('lt-LT')}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Month Filter:</span>
                <span class="info-value">{selectedMonth || 'All Months'}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Total Classes:</span>
                <span class="info-value">{classCount}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Total Revenue:</span>
                <span class="info-value">€{totalRevenue.toFixed(2)}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Total Teacher Payments:</span>
                <span class="info-value">€{totalTeacherPayments.toFixed(2)}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Net Profit:</span>
                <span class="info-value">€{(totalRevenue - totalTeacherPayments).toFixed(2)}</span>
            </div>
        </div>
        
        <div class="success-message">
            ✅ CSV file downloaded successfully with {classCount} classes!
        </div>
    {/if}
    
    <div class="csv-format-info">
        <h3>📋 CSV Format Details</h3>
        <ul>
            <li><strong>Class ID:</strong> Internal database ID for the class</li>
            <li><strong>Class Name:</strong> Name of the class (e.g., "Matematikos Pamoka | Tutory")</li>
            <li><strong>Class Date:</strong> Date and time of the class (YYYY-MM-DD HH:MM format)</li>
            <li><strong>Teacher Name:</strong> Full name of the teacher</li>
            <li><strong>Teacher ID:</strong> Internal database ID for the teacher</li>
            <li><strong>Total Student Price:</strong> Sum of actual charged amounts from matched BalanceTransaction records (using 3-level linking: related_class, Event ID, or name+date)</li>
            <li><strong>Teacher Payment:</strong> Amount paid to teacher for this class (calculated using <code>calculate_teacher_price_for_categorized_class</code>)</li>
            <li><strong>Student Count:</strong> Number of students enrolled in the class</li>
            <li><strong>Matched Charges Count:</strong> Number of BalanceTransaction charges successfully matched to this class</li>
            <li><strong>Google Calendar Event ID:</strong> Google Calendar event identifier</li>
        </ul>
    </div>
</div>

